import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { supabase, isSupabaseConfigured } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

export const CENTER_MODULES = [
  {
    key: "daily_activity",
    label: "Daily activity",
    description: "Daily reports, parent updates, and the entry point for Learning observations.",
  },
  {
    key: "photos",
    label: "Photos",
    description: "Classroom photos and parent photo views.",
  },
  {
    key: "documents",
    label: "Documents",
    description: "Family documents and forms.",
  },
  {
    key: "attendance",
    label: "Attendance",
    description: "Check-in, absences, and attendance notices.",
  },
  {
    key: "events",
    label: "Events",
    description: "Center events and family RSVPs.",
  },
  {
    key: "conferences",
    label: "Conferences",
    description: "Teacher conference scheduling and bookings.",
  },
  {
    key: "health_records",
    label: "Health records",
    description: "Health documents, medications, and pickup records.",
  },
  {
    key: "enrollment",
    label: "Enrollment",
    description: "Enrollment workflows and inquiries.",
  },
  {
    key: "messaging",
    label: "Messaging",
    description: "Family and classroom messages.",
  },
  {
    key: "family_billing",
    label: "Family billing",
    description: "Center tuition, invoices, and family payment records.",
  },
  {
    key: "public_site",
    label: "Public site",
    description: "Center pages, media, and public announcements.",
  },
  {
    key: "incidents",
    label: "Incidents",
    description: "Incident reports and parent acknowledgments.",
  },
  {
    key: "ratio_capacity",
    label: "Ratio & capacity",
    description: "Configured classroom coverage, capacity, and planning forecasts.",
  },
  {
    key: "food_program",
    label: "Food program",
    description: "Meal planning, attendance-informed counts, and food-program review.",
  },
  {
    key: "staffing",
    label: "Staffing",
    description: "Teacher schedules, timecards, time-off requests, and staffing review.",
  },
  {
    key: "learning",
    label: "Learning observations",
    description: "Teacher-noticed learning moments with administrator-reviewed family sharing. Requires Daily activity.",
  },
];

const DEFAULT_MODULES = Object.freeze(
  Object.fromEntries(CENTER_MODULES.map((module) => [module.key, true])),
);

const OrganizationConfigContext = createContext(null);

function mapModules(rows) {
  const next = { ...DEFAULT_MODULES };
  for (const row of rows || []) {
    if (Object.prototype.hasOwnProperty.call(next, row.module_key)) {
      next[row.module_key] = row.enabled !== false;
    }
  }
  return next;
}

export function OrganizationConfigProvider({ children }) {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id || null;
  const [state, setState] = useState({
    loading: true,
    organization: null,
    modules: DEFAULT_MODULES,
    error: null,
  });

  const load = useCallback(async () => {
    if (!isSupabaseConfigured || !organizationId) {
      setState({
        loading: false,
        organization: null,
        modules: DEFAULT_MODULES,
        error: null,
      });
      return;
    }

    setState((previous) => ({ ...previous, loading: true, error: null }));
    const [organizationResult, moduleResult] = await Promise.all([
      supabase
        .from("organizations")
        .select(
          "id, name, display_name, workflow_settings, feature_public_announcements, feature_enrollment_open, enforce_attendance_clearance",
        )
        .eq("id", organizationId)
        .maybeSingle(),
      supabase
        .from("organization_modules")
        .select("id, organization_id, module_key, enabled, configuration, updated_at")
        .eq("organization_id", organizationId),
    ]);

    // Local development environments that have not applied the tenancy
    // migration can still use every existing module. Production applies the
    // migration before this client bundle is deployed.
    const moduleTableMissing = moduleResult.error?.code === "42P01";
    const error = organizationResult.error || (moduleTableMissing ? null : moduleResult.error);
    if (error) {
      setState((previous) => ({ ...previous, loading: false, error }));
      return;
    }

    setState({
      loading: false,
      organization: organizationResult.data || null,
      modules: mapModules(moduleResult.data),
      error: null,
    });
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const setModuleEnabled = useCallback(
    async (moduleKey, enabled) => {
      if (!isSupabaseConfigured || !organizationId) {
        throw new Error("Center modules require a live HoneyAcorn environment.");
      }
      if (!Object.prototype.hasOwnProperty.call(DEFAULT_MODULES, moduleKey)) {
        throw new Error("Unsupported center module.");
      }
      const { data, error } = await supabase
        .from("organization_modules")
        .upsert(
          {
            organization_id: organizationId,
            module_key: moduleKey,
            enabled: Boolean(enabled),
          },
          { onConflict: "organization_id,module_key" },
        )
        .select("id, organization_id, module_key, enabled, configuration, updated_at")
        .single();
      if (error) throw error;
      setState((previous) => ({
        ...previous,
        modules: { ...previous.modules, [moduleKey]: data.enabled !== false },
      }));
      return data;
    },
    [organizationId],
  );

  const value = useMemo(
    () => ({
      organizationId,
      organization: state.organization,
      modules: state.modules,
      loading: state.loading,
      error: state.error,
      isModuleEnabled: (moduleKey) => state.modules[moduleKey] !== false,
      setModuleEnabled,
      refreshOrganizationConfig: load,
    }),
    [organizationId, state, setModuleEnabled, load],
  );

  return (
    <OrganizationConfigContext.Provider value={value}>
      {children}
    </OrganizationConfigContext.Provider>
  );
}

export function useOrganizationConfig() {
  const context = useContext(OrganizationConfigContext);
  if (!context) {
    throw new Error("useOrganizationConfig must be used within OrganizationConfigProvider");
  }
  return context;
}
